import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { GripVertical, Trash2, Copy, AlertCircle, Square, RectangleVertical, RectangleHorizontal, Layers, Wrench, Package, Brush } from 'lucide-react';
import type { LayerV2 } from '../types';
import type { DatabaseMaterial } from '@/hooks/useMaterials';
import type { ScrewSuggestion } from '../hooks/screwRecommendation';
import LayerScrewSelectorV2 from './LayerScrewSelectorV2';
import LayerAnchorSelector from './LayerAnchorSelector';
import { formatNumber } from '@/utils/format';

interface LayerCardProps {
  layer: LayerV2;
  /** Suggestion vite per questo layer (presente solo se category=board). */
  screwSuggestion?: ScrewSuggestion;
  onUpdate: (patch: Partial<LayerV2>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onClickMaterial: () => void;
  /** Override manuale vite — disattiva auto-suggest. */
  onChangeScrew?: (screw: DatabaseMaterial | null) => void;
  /** Override manuale quantità viti/m². */
  onChangeScrewQty?: (qty: number) => void;
  /** Riattiva l'auto-suggest e applica la vite raccomandata. */
  onReapplyScrewSuggestion?: () => void;
  /** Tassello di fissaggio (solo per layer guida). */
  onChangeAnchor?: (anchor: DatabaseMaterial | null) => void;
  /** ml di guida per m² di parete — serve a derivare il numero di tasselli. */
  guideMlPerSqm?: number;
  /**
   * Mostra il campo "Consumo" (quantità di materiale per m²).
   *
   * Default `false`: la card è condivisa con i controsoffitti, dove
   * `computeCeilingCosts` tiene la quantità fissa a 1 e il salvataggio non
   * porta `materialQuantity`. Mostrarlo là darebbe un campo che accetta il
   * valore, lo cancella al salvataggio e non cambia un centesimo — peggio che
   * non averlo. Lo accende solo il pannello delle pareti.
   */
  showConsumo?: boolean;
}

/**
 * Card singola di un layer nella composizione, con drag handle dnd-kit.
 *
 * Vedi `docs/mockupui.md` §7 per anatomia card.
 */
const LayerCard: React.FC<LayerCardProps> = ({
  layer,
  screwSuggestion,
  onUpdate,
  onRemove,
  onDuplicate,
  onClickMaterial,
  onChangeScrew,
  onChangeScrewQty,
  onReapplyScrewSuggestion,
  onChangeAnchor,
  guideMlPerSqm = 0,
  showConsumo = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: layer.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const isIncomplete = !layer.materialId || layer.thickness <= 0;

  // I listini quotano la CONFEZIONE: "GYPSOMAF - 10 kg" costa 4,148 € netti a
  // sacco, non al chilo. `box_pieces` dice quante unità ci sono dentro.
  const confezione = Number(layer.material?.box_pieces ?? 0);
  const prezzoConfezione = Number(layer.material?.net_price ?? layer.material?.unit_price ?? 0);
  const prezzoPerUnita = confezione > 0 ? prezzoConfezione / confezione : prezzoConfezione;
  const incidenzaCatalogo = Number(layer.material?.incidence_per_sqm ?? 1) || 1;
  // Stessa identica regola del motore (`computeStratigraphyCosts`): un consumo
  // nullo o negativo NON e` un consumo, si ricade sull'incidenza di catalogo.
  // Se la card usasse `?? incidenzaCatalogo` mostrerebbe €0,00/m² scrivendo 0,
  // mentre il preventivo continuerebbe a fatturare l'incidenza.
  const consumoDichiarato = Number(layer.materialQuantity ?? NaN);
  const consumoEffettivo =
    Number.isFinite(consumoDichiarato) && consumoDichiarato > 0 ? consumoDichiarato : incidenzaCatalogo;
  const unita = String(layer.material?.unit ?? '').toLowerCase();
  const vendutoASuperficie = unita === 'mq' || unita === 'm2' || unita === 'm²' || unita === '';
  // Il prezzo mostrato e` quello dell'UNITA` CONTENUTA, non della confezione: se
  // l'unita` dichiarata e` "scatola" scriverci accanto "/scatola" direbbe il
  // falso di un fattore pari ai pezzi per scatola. Per gli altri (kg, ml, pz)
  // l'unita` e` gia` quella giusta.
  const unitaPrezzo = confezione > 0 && unita === 'scatola' ? 'pz' : (layer.material?.unit ?? '—');
  // Il consumo è modificabile solo dove il motore lo legge davvero: lastre,
  // pannelli soffitto e finiture. Su montanti e guide la quantità è derivata
  // dal passo e dall'altezza — un campo qui direbbe una bugia.
  const consumoModificabile =
    showConsumo &&
    !vendutoASuperficie &&
    (layer.material?.category === 'finish' ||
      layer.material?.category === 'board' ||
      layer.material?.category === 'ceiling_tile');

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`relative ${isIncomplete ? 'border-amber-300 bg-amber-50/30' : ''} ${
        isDragging ? 'shadow-lg ring-2 ring-primary/40' : 'shadow-sm hover:shadow-md transition-shadow'
      }`}
    >
      <div className="flex items-stretch">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="flex items-center px-2 cursor-grab active:cursor-grabbing text-zinc-400 hover:text-zinc-700 focus:outline-none focus:ring-2 focus:ring-primary rounded-l"
          aria-label={`Trascina layer ${layer.position}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Contenuto */}
        <div className="flex-1 p-3 min-w-0">
          {/* Header: posizione + nome materiale (cliccabile) */}
          <button
            onClick={onClickMaterial}
            className="w-full text-left flex items-start gap-2 group"
          >
            <CategoryIcon category={layer.material?.category} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono text-muted-foreground">#{layer.position}</span>
                {layer.material ? (
                  <>
                    <span className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                      {layer.material.name}
                    </span>
                    <Badge variant="outline" className="text-[9px] uppercase">
                      {categoryShortLabel(layer.material.category)}
                    </Badge>
                  </>
                ) : (
                  <span className="text-sm text-amber-700 inline-flex items-center gap-1 group-hover:text-amber-900">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Clicca per scegliere materiale
                  </span>
                )}
              </div>
              {layer.material && (
                <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                  {layer.material.supplier}
                  {layer.material.material_type && ` · ${layer.material.material_type}`}
                </div>
              )}
            </div>
          </button>

          {/* Spessore + costo */}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <label className="text-[11px] text-muted-foreground">Spessore</label>
              <Input
                type="number"
                value={layer.thickness}
                onChange={(e) => onUpdate({ thickness: Number(e.target.value) || 0 })}
                className="h-7 w-20 text-xs"
                step="0.5"
                min="0"
                aria-label={`Spessore layer ${layer.position}`}
              />
              <span className="text-[11px] text-muted-foreground">mm</span>
            </div>
            {layer.material && (
              <div className="text-[11px] text-muted-foreground">
                <span className="font-medium text-green-700">
                  €{formatNumber(prezzoPerUnita)}/{unitaPrezzo}
                </span>
                {confezione > 0 && (
                  <span className="ml-1 text-zinc-400">
                    (conf. {formatNumber(confezione, 0)} {layer.material.unit})
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Consumo al m²: solo per i materiali che non si vendono a superficie
              (malte, stucchi, primer). Su una lastra il consumo è 1 m²/m² e il
              campo sarebbe solo rumore. */}
          {layer.material && consumoModificabile && (
            <div className="flex items-center gap-1.5 mt-2">
              <label className="text-[11px] text-muted-foreground">Consumo</label>
              <Input
                type="number"
                value={layer.materialQuantity ?? ''}
                placeholder={formatNumber(incidenzaCatalogo)}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  const n = Number(v.replace(',', '.'));
                  onUpdate({ materialQuantity: v === '' || !Number.isFinite(n) ? null : n });
                }}
                className="h-7 w-20 text-xs"
                step="0.1"
                min="0"
                aria-label={`Consumo materiale layer ${layer.position}`}
              />
              <span className="text-[11px] text-muted-foreground">
                {layer.material.unit}/m²
                {consumoEffettivo === incidenzaCatalogo && ' · da catalogo'}
              </span>
              <span className="text-[11px] font-medium text-green-700">
                = €{formatNumber(prezzoPerUnita * consumoEffettivo)}/m²
              </span>
            </div>
          )}

          {/* Selettore vite (solo per layer board con materiale assegnato) */}
          {layer.material?.category === 'board' && layer.materialId && onChangeScrew && onChangeScrewQty && onReapplyScrewSuggestion && (
            <LayerScrewSelectorV2
              layer={layer}
              suggestion={screwSuggestion}
              onChangeScrew={onChangeScrew}
              onChangeQuantity={onChangeScrewQty}
              onReapplySuggestion={onReapplyScrewSuggestion}
            />
          )}

          {/* Selettore tassello (solo per layer guida con materiale assegnato) */}
          {layer.material?.category === 'structure_guide' && layer.materialId && onChangeAnchor && (
            <LayerAnchorSelector
              layer={layer}
              guideMlPerSqm={guideMlPerSqm}
              onChangeAnchor={onChangeAnchor}
            />
          )}
        </div>

        {/* Action buttons (duplicate + delete) */}
        <div className="flex flex-col items-stretch border-l border-zinc-100">
          <button
            onClick={onDuplicate}
            className="px-2 py-1 text-zinc-400 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary rounded-tr"
            aria-label={`Duplica layer ${layer.position}`}
            title="Duplica layer"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={onRemove}
            className="px-2 py-1 text-zinc-400 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-destructive rounded-br"
            aria-label={`Elimina layer ${layer.position}`}
            title="Elimina layer"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </Card>
  );
};

const CategoryIcon: React.FC<{ category?: string }> = ({ category }) => {
  const cls = 'h-4 w-4 mt-0.5';
  switch (category) {
    case 'board': return <Square className={`${cls} text-amber-600`} />;
    case 'ceiling_tile': return <Square className={`${cls} text-amber-700`} />;
    case 'structure_frame': return <RectangleVertical className={`${cls} text-zinc-500`} />;
    case 'structure_guide': return <RectangleHorizontal className={`${cls} text-zinc-500`} />;
    case 'insulation': return <Layers className={`${cls} text-yellow-600`} />;
    case 'screw': return <Wrench className={`${cls} text-zinc-700`} />;
    case 'finish': return <Brush className={`${cls} text-purple-600`} />;
    case 'accessory': return <Package className={`${cls} text-blue-600`} />;
    default: return <Square className={`${cls} text-zinc-300`} />;
  }
};

function categoryShortLabel(category: string): string {
  const map: Record<string, string> = {
    board: 'Lastra',
    ceiling_tile: 'Pannello',
    structure_frame: 'Mont.',
    structure_guide: 'Guida',
    insulation: 'Isol.',
    screw: 'Vite',
    finish: 'Finit.',
    accessory: 'Acc.',
    other: 'Altro',
  };
  return map[category] ?? category;
}

export default LayerCard;
